import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Alert, Menu, MenuItem } from '@mui/material';
import LoginPage from '../Login/Login';
import { getUserProfile, userLogout, fetchMySubscriptions } from '../../redux/network/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { setAlertMessage, setAlertType } from '../../redux/reducers/alertSlice';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import { clear_User } from '../../redux/reducers/userSlice';
import logoSvg from './logo-new.svg';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import ReactGA from 'react-ga4';
import { Menu as MenuIcon, X, Sun, Moon } from 'lucide-react';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}));



export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignupRoute = location.pathname === '/signup';
  const isPlaygroundRoute = location.pathname === '/SmartInsights';
  const { isLoginDialog } = useSelector(state => state.login);
  const [openDialog, setOpenDialog] = useState(false);
  const [token, setToken] = useState("");
  const jwtToken = localStorage.getItem("BQS_TOKEN");
  const env = process.env.REACT_APP_ENV;
  const dispatch = useDispatch();
  const alertData = useSelector(state => state.alert);
  const { alertMessage, alertType } = alertData;
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [standaloneData, setStandaloneData] = useState({});
  const [colorChange, setColorChange] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const themePhaseTimeoutRef = useRef(null);
  const themeCleanupTimeoutRef = useRef(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('smartinsights_theme') || 'light');
  const isLightTheme = theme === 'light';
  const subscriptionsState = useSelector((state) => state.user.subscriptions);
  const activeSubs = subscriptionsState.value || [];
  const isSubscribed = activeSubs.length > 0;

  const toggleTheme = (e) => {
    e.stopPropagation();
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    const directionClass = newTheme === 'light' ? 'theme-to-light' : 'theme-to-dark';
    document.body.classList.remove('theme-transitioning', 'theme-to-light', 'theme-to-dark', 'theme-phase-out', 'theme-phase-in');
    void document.body.offsetWidth;
    document.body.classList.add('theme-transitioning', directionClass, 'theme-phase-out');
    if (themePhaseTimeoutRef.current) {
      window.clearTimeout(themePhaseTimeoutRef.current);
    }
    if (themeCleanupTimeoutRef.current) {
      window.clearTimeout(themeCleanupTimeoutRef.current);
    }

    themePhaseTimeoutRef.current = window.setTimeout(() => {
      setTheme(newTheme);
      localStorage.setItem('smartinsights_theme', newTheme);
      document.body.classList.remove('theme-phase-out');
      document.body.classList.add('theme-phase-in');
      themePhaseTimeoutRef.current = null;
    }, 110);

    themeCleanupTimeoutRef.current = window.setTimeout(() => {
      document.body.classList.remove('theme-transitioning', 'theme-to-light', 'theme-to-dark', 'theme-phase-out', 'theme-phase-in');
      themeCleanupTimeoutRef.current = null;
    }, 420);
  };

  useEffect(() => {
    document.body.classList.toggle('smartinsights-theme-active', isPlaygroundRoute);
    if (theme === 'light' && isPlaygroundRoute) {
      document.body.classList.add('light-theme');
    } else {
      document.body.classList.remove('light-theme');
    }

    return () => {
      if (!isPlaygroundRoute) {
        document.body.classList.remove('smartinsights-theme-active');
        document.body.classList.remove('light-theme');
      }
    };
  }, [theme, isPlaygroundRoute]);

  useEffect(() => {
    localStorage.setItem('smartinsights_theme', theme);
  }, [theme]);

  useEffect(() => {
    return () => {
      if (themePhaseTimeoutRef.current) {
        window.clearTimeout(themePhaseTimeoutRef.current);
      }
      if (themeCleanupTimeoutRef.current) {
        window.clearTimeout(themeCleanupTimeoutRef.current);
      }
      document.body.classList.remove('theme-transitioning', 'theme-to-light', 'theme-to-dark', 'theme-phase-out', 'theme-phase-in');
      document.body.classList.remove('smartinsights-theme-active');
    };
  }, []);

  const userData = useSelector((state) => state.user.profile);
  const { value } = userData || {};
  const profileData = value?.profileData || {};
  const membershipProfile = profileData?.membershipProfile;

  const firstName = typeof profileData?.name === 'object' ? profileData?.name?.firstName : profileData?.name;
  const profilePic = profileData?.profilePic;
  const userRole = localStorage.getItem('BQS_ROLE');

  // Subscription detection logic
  useEffect(() => {
    if (!jwtToken || userRole === 'coach') return;
    const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
    if (enforcement === 'off') return;
    dispatch(fetchMySubscriptions(jwtToken, "active"));
  }, [jwtToken, userRole, dispatch]);

  const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
  const isEnforced = enforcement === 'hard';
  const hasActiveSubscription = !isEnforced || isSubscribed || (membershipProfile && (membershipProfile.isActive === true || (membershipProfile.remainingReviews ?? 0) > 0));
  const shouldShowDashboardLink = jwtToken && (userRole === 'coach' || hasActiveSubscription);

  const profileRef = useRef(null);

  const transparentRoutes = ['/', '/about', '/products', '/products/smart-assessments', '/products/smart-insights', '/products/smart-journals', '/case-studies', '/solutions', '/customers', '/careers', '/update-profile', '/signup', '/news', '/privacy_policy', '/term_service', '/refund_policy'];
  const isTransparentRoute = transparentRoutes.includes(location.pathname);

  const hoverClass = (isTransparentRoute && !colorChange) ? 'hover:text-electric-lime' : 'hover:text-black';

  useEffect(() => {
    // Do not show login dialog over the signup page or playground page
    if (isSignupRoute || isPlaygroundRoute) {
      setOpenDialog(false);
      return;
    }
    setOpenDialog(isLoginDialog);
  }, [isLoginDialog, isSignupRoute, isPlaygroundRoute]);

  useEffect(() => {
    if (alertMessage && alertType) {
      const redirectTimeout = setTimeout(() => {
        dispatch(setAlertMessage(""));
        dispatch(setAlertType(""));
      }, alertData.alertTime);
      return () => clearTimeout(redirectTimeout);
    }
  }, [alertMessage, alertType, dispatch]);

  useEffect(() => {
    if (!jwtToken || env === "standalone" || isSignupRoute) return;
    const hash = typeof window !== 'undefined' ? window.location.hash || '' : '';
    if (hash.includes('update-profile') && hash.includes('biginId=')) return;
    dispatch(getUserProfile(jwtToken));
  }, [jwtToken, dispatch, env, isSignupRoute]);

  useEffect(() => {
    const changeNavbarColor = () => {
      setColorChange(window.scrollY >= 15);
    };
    window.addEventListener("scroll", changeNavbarColor);
    return () => window.removeEventListener("scroll", changeNavbarColor);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setAnchorEl(null);
        setDropdownVisible(false);
      } else if (!isSignupRoute && !isPlaygroundRoute && profileRef.current && profileRef.current.contains(event.target) && !jwtToken) {
        // Ensure login dialog opens if not logged in, except on signup route
        dispatch(setIsLoginDialog(true));
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [jwtToken, dispatch, isSignupRoute, isPlaygroundRoute]);

  const handleCloseDialog = () => {
    dispatch(setIsLoginDialog(false));
    setOpenDialog(false);
  };

  const handleOnClickLogo = () => {
    try { ReactGA.event({ category: 'Nav', action: 'logo_click' }); } catch { }
    navigate("/");
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    try { ReactGA.event({ category: 'Nav', action: 'profile_click' }); } catch { }
    // If Explore Playground (trial) user on playground, go straight to Sign Up
    try {
      const isTrialExplore = sessionStorage.getItem('PG_TRIAL_MODE') === '1';
      if (!jwtToken && isPlaygroundRoute && isTrialExplore) {
        sessionStorage.setItem('openPreSignOnReturn', '1');
        sessionStorage.setItem('openPreSignStep', '3');
        try { ReactGA.event({ category: 'Nav', action: 'signup_redirect_from_playground' }); } catch { }
        navigate('/signup');
        return;
      }
    } catch { }
    if (isSignupRoute) {
      // Do not open login dialog from Nav while signing up
      return;
    }
    if (jwtToken) {
      try { ReactGA.event({ category: 'Nav', action: 'account_menu_toggle', label: window.innerWidth > 768 ? 'desktop' : 'mobile' }); } catch { }
      if (window.innerWidth > 768) {
        setAnchorEl(anchorEl ? null : e.currentTarget); // Toggle Menu
      } else {
        setDropdownVisible(!dropdownVisible);
      }
    } else {
      try { ReactGA.event({ category: 'Auth', action: 'login_open' }); } catch { }
      dispatch(setIsLoginDialog(true));
    }
  };

  const generateToken = () => {
    const userID = "user123";
    const newToken = btoa(`${Date.now()}-${Math.random()}`);
    localStorage.setItem("BQS_TOKEN", newToken);
    localStorage.setItem("BQS__ID", userID);
    setToken(newToken);
  };

  const chooseUsers = (e) => {
    e.stopPropagation();
    setDropdownVisible(!dropdownVisible);
    if (!localStorage.getItem("BQS_TOKEN")) {
      generateToken();
    }
  };

  const selectRole = (role) => {
    const userID = localStorage.getItem("BQS__ID");
    const data = {
      userRole: role,
      id: userID,
      token: localStorage.getItem("BQS_TOKEN"),
    };
    setStandaloneData(data);
    setSelectedRole(role);
    setDropdownVisible(false);
  };

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    try { ReactGA.event({ category: 'Auth', action: 'logout' }); } catch { }
    userLogout(jwtToken)
      .then(res => res.json())
      .then(() => {
        sessionStorage.clear();
        localStorage.clear();
        dispatch(clear_User());
        dispatch(setIsLoginDialog(true));
        navigate("/");
      })
      .catch(() => {
        sessionStorage.clear();
        localStorage.clear();
        dispatch(clear_User());
        dispatch(setIsLoginDialog(true));
        navigate("/");
      })
      .finally(() => setAnchorEl(null));
  };

  const ProfileIcon = () => (
    <svg
      viewBox="0 0 53 53"
      fill="#FFFFFF"
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.61799 43.2478L10.1688 41.9365C12.5603 40.2146 15.0982 38.8538 17.7823 37.854C20.4646 36.8524 23.3701 36.3516 26.4988 36.3516C29.6256 36.3516 32.5311 36.8524 35.2153 37.854C37.8975 38.8538 40.4354 40.2137 42.8288 41.9337L44.3407 43.2918C48.4698 38.9064 51 32.9987 51 26.5C51 12.969 40.031 2 26.5 2C12.969 2 2 12.969 2 26.5C2 32.977 4.51336 38.8669 8.61799 43.2478ZM53 26.5C53 41.1355 41.1355 53 26.5 53C11.8645 53 0 41.1355 0 26.5C0 11.8645 11.8645 0 26.5 0C41.1355 0 53 11.8645 53 26.5ZM20.5003 25.4666C22.1303 27.0948 24.1298 27.9088 26.4988 27.9088C28.8679 27.9088 30.8674 27.0948 32.4974 25.4666C34.1255 23.8366 34.9396 21.8371 34.9396 19.4681C34.9396 17.0991 34.1255 15.0995 32.4974 13.4695C30.8674 11.8414 28.8679 11.0273 26.4988 11.0273C24.1298 11.0273 22.1303 11.8414 20.5003 13.4695C18.8722 15.0995 18.0581 17.0991 18.0581 19.4681C18.0581 21.8371 18.8722 23.8366 20.5003 25.4666Z"
      />
    </svg>
  );

  const MobileProfileIcon = () => (
    <svg
      viewBox="0 0 53 53"
      fill="#FFFFFF"
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.61799 43.2478L10.1688 41.9365C12.5603 40.2146 15.0982 38.8538 17.7823 37.854C20.4646 36.8524 23.3701 36.3516 26.4988 36.3516C29.6256 36.3516 32.5311 36.8524 35.2153 37.854C37.8975 38.8538 40.4354 40.2137 42.8288 41.9337L44.3407 43.2918C48.4698 38.9064 51 32.9987 51 26.5C51 12.969 40.031 2 26.5 2C12.969 2 2 12.969 2 26.5C2 32.977 4.51336 38.8669 8.61799 43.2478ZM53 26.5C53 41.1355 41.1355 53 26.5 53C11.8645 53 0 41.1355 0 26.5C0 11.8645 11.8645 0 26.5 0C41.1355 0 53 11.8645 53 26.5ZM20.5003 25.4666C22.1303 27.0948 24.1298 27.9088 26.4988 27.9088C28.8679 27.9088 30.8674 27.0948 32.4974 25.4666C34.1255 23.8366 34.9396 21.8371 34.9396 19.4681C34.9396 17.0991 34.1255 15.0995 32.4974 13.4695C30.8674 11.8414 28.8679 11.0273 26.4988 11.0273C24.1298 11.0273 22.1303 11.8414 20.5003 13.4695C18.8722 15.0995 18.0581 17.0991 18.0581 19.4681C18.0581 21.8371 18.8722 23.8366 20.5003 25.4666Z"
      />
    </svg>
  );

  return (
    <>
      <nav className="hidden lg:block fixed top-0 left-0 right-0 z-50 transition-all duration-500">
        <div className={`transition-all duration-500 border-b ${isTransparentRoute ? (colorChange ? 'backdrop-blur-xl bg-emerald/70 border-white/10' : 'bg-transparent border-transparent') : 'backdrop-blur-xl bg-emerald/90 border-white/10'}`}>
          <div className="container mx-auto px-1 lg:px-2">
            <div className="flex items-center justify-between h-24">
              {/* Logo */}
              <div className="cursor-pointer transition-all duration-300 hover:scale-105 flex items-center gap-3" onClick={handleOnClickLogo}>
                <img src="/logo192.png" alt="logo icon" className="h-10 w-10" />
                <span className="text-white text-2xl font-semibold tracking-wide">VISIST.AI</span>
              </div>

              {/* Alert */}


              {/* Desktop Nav */}
              <div className="flex items-center gap-3 lg:gap-8 xl:gap-12">
                {/* Centered Navigation Links */}
                <div className="hidden xl:flex items-center justify-center gap-8 absolute left-1/2 -translate-x-1/2">
                  {/* Home - Standalone Link */}
                  <NavLink
                    to="/"
                    onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'home' }); } catch { } }}
                    className={({ isActive }) => `${isActive ? (isTransparentRoute && !colorChange ? 'text-electric-lime' : 'text-black') : 'text-white/80'} ${hoverClass} font-bold text-sm tracking-wide transition-all duration-300`}
                  >
                    Home
                  </NavLink>

                  {/* Products - Standalone Link */}
                  <NavLink
                    to="/products"
                    onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'products' }); } catch { } }}
                    className={({ isActive }) => `${isActive ? (isTransparentRoute && !colorChange ? 'text-electric-lime' : 'text-black') : 'text-white/80'} ${hoverClass} font-bold text-sm tracking-wide transition-all duration-300`}
                  >
                    Products
                  </NavLink>

                  {/* Solutions Link */}
                  <NavLink
                    to="/solutions"
                    onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'solutions' }); } catch { } }}
                    className={({ isActive }) => `${isActive ? (isTransparentRoute && !colorChange ? 'text-electric-lime' : 'text-black') : 'text-white/80'} ${hoverClass} font-bold text-sm tracking-wide transition-all duration-300`}
                  >
                    Solutions
                  </NavLink>

                  {/* Company Dropdown */}
                  <div
                    className="relative"
                    onMouseEnter={() => setCompanyDropdownOpen(true)}
                    onMouseLeave={() => setCompanyDropdownOpen(false)}
                  >
                    <button
                      className={`${(location.pathname === '/about' || location.pathname === '/careers' || location.pathname === '/news' || location.pathname === '/support') ? (isTransparentRoute && !colorChange ? 'text-electric-lime' : 'text-black') : 'text-white/80'} ${hoverClass} font-bold text-sm tracking-wide transition-all duration-300 flex items-center gap-1`}
                    >
                      Company
                      <svg className={`w-3 h-3 transition-transform duration-300 ${companyDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {companyDropdownOpen && (
                      <div className="absolute top-full left-0 w-56 bg-[#0a0a0a]/95 backdrop-blur-xl rounded-b-xl shadow-2xl border border-white/10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50" style={{ marginTop: '-2px' }}>
                        <div className="p-2">
                          <NavLink
                            to="/about"
                            onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'about_us' }); } catch { } setCompanyDropdownOpen(false); }}
                            className={({ isActive }) => `block px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald/20 text-[#ccff00] shadow-glow-green' : 'text-white/90 hover:bg-white/5 hover:text-[#ccff00]'}`}
                          >
                            About Us
                          </NavLink>
                          <NavLink
                            to="/careers"
                            onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'join_our_team' }); } catch { } setCompanyDropdownOpen(false); }}
                            className={({ isActive }) => `block px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald/20 text-[#ccff00] shadow-glow-green' : 'text-white/90 hover:bg-white/5 hover:text-[#ccff00]'}`}
                          >
                            Careers
                          </NavLink>
                          <NavLink
                            to="/news"
                            onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'news' }); } catch { } setCompanyDropdownOpen(false); }}
                            className={({ isActive }) => `block px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald/20 text-[#ccff00] shadow-glow-green' : 'text-white/90 hover:bg-white/5 hover:text-[#ccff00]'}`}
                          >
                            News
                          </NavLink>
                          <NavLink
                            to="/support"
                            onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: 'support' }); } catch { } setCompanyDropdownOpen(false); }}
                            className={({ isActive }) => `block px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${isActive ? 'bg-emerald/20 text-[#ccff00] shadow-glow-green' : 'text-white/90 hover:bg-white/5 hover:text-[#ccff00]'}`}
                          >
                            Support
                          </NavLink>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desktop Nav fallback for smaller desktops - normal flex distribution */}
                <div className="hidden lg:flex xl:hidden items-center justify-center gap-6">
                  <NavLink to="/" className={({ isActive }) => `${isActive ? 'text-electric-lime' : 'text-white/80'} font-bold text-xs tracking-wide transition-colors`}>Home</NavLink>
                  <NavLink to="/products" className={({ isActive }) => `${isActive ? 'text-electric-lime' : 'text-white/80'} font-bold text-xs tracking-wide transition-colors`}>Products</NavLink>
                  <NavLink to="/solutions" className={({ isActive }) => `${isActive ? 'text-electric-lime' : 'text-white/80'} font-bold text-xs tracking-wide transition-colors`}>Solutions</NavLink>
                  {/* Company simplified for smaller screens */}
                  <NavLink to="/about" className={({ isActive }) => `${isActive ? 'text-electric-lime' : 'text-white/80'} font-bold text-xs tracking-wide transition-colors`}>About</NavLink>
                  <NavLink to="/support" className={({ isActive }) => `${isActive ? 'text-electric-lime' : 'text-white/80'} font-bold text-xs tracking-wide transition-colors`}>Support</NavLink>
                </div>

                {/* My Reviews / SmartJournal - Always positioned before utilities */}
                {shouldShowDashboardLink && (
                  <NavLink
                    to={userRole === "player" ? "/game-posts" : "/coach_flow/reviewd-video"}
                    onClick={() => { try { ReactGA.event({ category: 'Nav', action: 'nav_click', label: userRole === "player" ? 'my_dashboard' : 'my_reviews' }); } catch { } }}
                    className={({ isActive }) => `hidden lg:block ${isActive ? (isTransparentRoute && !colorChange ? 'text-electric-lime' : 'text-black') : 'text-white/80'} ${hoverClass} font-bold text-sm tracking-wide transition-all duration-300`}
                  >
                    {userRole === "player" ? "My SmartJournal" : "My Reviews"}
                  </NavLink>
                )}

                {/* Theme Toggle - Only in SmartInsights */}
                {isPlaygroundRoute && (
                  <button
                    onClick={toggleTheme}
                    className={`group relative inline-flex h-11 w-[74px] items-center rounded-full border px-1.5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#00b140] focus:ring-offset-2 focus:ring-offset-transparent ${isLightTheme
                        ? 'bg-white/92 border-[#d8e7dc] shadow-[0_8px_24px_rgba(255,255,255,0.14)]'
                        : 'bg-[#102016]/90 border-white/16 shadow-[0_8px_24px_rgba(0,0,0,0.26)]'
                      }`}
                    title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
                    aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                  >
                    <span
                      className={`absolute top-1/2 h-8 w-8 -translate-y-1/2 rounded-full transition-all duration-300 ${isLightTheme
                          ? 'translate-x-[32px] bg-gradient-to-br from-[#ffffff] to-[#edf7f0] shadow-[0_6px_16px_rgba(2,107,51,0.16)]'
                          : 'translate-x-0 bg-gradient-to-br from-[#173323] to-[#0c1b13] shadow-[0_6px_16px_rgba(0,0,0,0.3)]'
                        }`}
                      aria-hidden="true"
                    />
                    <span className="relative z-10 flex w-full items-center justify-between px-1">
                      <span className={`transition-colors duration-300 ${isLightTheme ? 'text-[#7c9686]' : 'text-[#ffd86c]'}`}>
                        <Sun size={15} strokeWidth={2.2} />
                      </span>
                      <span className={`transition-colors duration-300 ${isLightTheme ? 'text-[#11251a]' : 'text-[#a3f0c0]'}`}>
                        <Moon size={15} strokeWidth={2.2} />
                      </span>
                    </span>
                  </button>
                )}

                {/* Profile/Login - Pill Button */}
                <div className="relative" onClick={env === "standalone" ? chooseUsers : handleLoginClick} ref={profileRef} style={{ zIndex: 1000 }} role="button" tabIndex={0}>
                  <LightTooltip title={
                    (() => {
                      if (env === 'standalone') return (selectedRole || 'Login');
                      try {
                        const isTrialExplore = sessionStorage.getItem('PG_TRIAL_MODE') === '1';
                        if (!jwtToken && isPlaygroundRoute && isTrialExplore) return 'Sign Up';
                      } catch { }
                      return (jwtToken && firstName ? firstName : 'Login');
                    })()
                  } placement="bottom-end">
                    <div className="px-4 py-2 rounded-lg bg-[#339733] border border-[#339733] backdrop-blur-sm cursor-pointer transition-all duration-300 hover:border-[#339733] hover:shadow-glow-green">
                      <span className="text-white font-semibold text-sm">{(() => {
                        if (env === 'standalone') return (selectedRole || 'Sign In');
                        try {
                          const isTrialExplore = sessionStorage.getItem('PG_TRIAL_MODE') === '1';
                          if (!jwtToken && isPlaygroundRoute && isTrialExplore) return 'Sign Up';
                        } catch { }
                        return (jwtToken ? (firstName || 'Account') : 'Sign In');
                      })()}</span>
                      {jwtToken && (
                        <span className="ml-1 inline-block">
                          <svg className="w-3 h-3 inline transition-transform duration-300" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 4L6 8L10 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </LightTooltip>
                  {jwtToken && anchorEl && (
                    <div className="fixed w-56 bg-[#0a0a0a]/95 backdrop-blur-xl rounded-b-xl shadow-2xl border border-white/10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-[1100]"
                      style={{
                        top: anchorEl ? `${anchorEl.getBoundingClientRect().bottom - 2}px` : 'auto',
                        right: anchorEl ? `${window.innerWidth - anchorEl.getBoundingClientRect().right}px` : 'auto'
                      }}
                    >
                      <div className="p-2">
                        {/* Only show Edit Profile if not coach */}
                        {userRole !== 'coach' && (
                          <div
                            onClick={() => { navigate("/profile/personal_details"); handleClose(); }}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 text-white/90 hover:bg-white/5 hover:text-[#ccff00] group"
                          >
                            <svg className="w-5 h-5 transition-colors" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.40005Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className="text-sm font-semibold">Edit Profile</span>
                          </div>
                        )}

                        {/* Logout */}
                        <div
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 text-red-400 hover:bg-red-500/10 group"
                        >
                          <svg className="w-5 h-5 transition-colors" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 17L15 12L10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15 12H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span className="text-sm font-semibold">Logout</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* Alert - Global */}
      <div className="fixed top-28 left-1/2 -translate-x-1/2 z-[10000] w-full max-w-md px-4">
        {alertMessage && alertType && (
          <Alert severity={alertType} className="backdrop-blur-xl shadow-2xl">
            {alertMessage}
          </Alert>
        )}
      </div>
      {(openDialog && !isSignupRoute && !isPlaygroundRoute) && (
        <LoginPage onCancelDialog={handleCloseDialog} />
      )}
      <header className={`lg:hidden fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isTransparentRoute ? (colorChange ? 'backdrop-blur-xl bg-emerald/70 border-white/20' : 'bg-transparent border-transparent') : 'backdrop-blur-xl bg-emerald/90 border-white/20'}`}>
        <div className="px-3 sm:px-4 md:px-6 py-3 relative">
          <div className="flex items-center justify-between gap-1 sm:gap-2 overflow-x-auto overflow-y-visible">
            <Link to="/" onClick={handleOnClickLogo} className="transition-transform duration-300 hover:scale-105 flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              <img src="/logo192.png" alt="logo icon" className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="text-white text-sm sm:text-base font-bold tracking-wide">VISIST.AI</span>
            </Link>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {isPlaygroundRoute && (
                <button
                  onClick={toggleTheme}
                  className={`group relative inline-flex h-8 w-[58px] sm:h-9 sm:w-[62px] items-center rounded-full border px-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#00b140] flex-shrink-0 ${isLightTheme
                      ? 'bg-white/92 border-[#d8e7dc] shadow-[0_6px_18px_rgba(255,255,255,0.12)]'
                      : 'bg-[#102016]/90 border-white/16 shadow-[0_6px_18px_rgba(0,0,0,0.24)]'
                    }`}
                  title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
                  aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  <span
                    className={`absolute top-1/2 h-6 w-6 sm:h-7 sm:w-7 -translate-y-1/2 rounded-full transition-all duration-300 ${isLightTheme
                        ? 'translate-x-[24px] sm:translate-x-[26px] bg-gradient-to-br from-[#ffffff] to-[#edf7f0] shadow-[0_5px_14px_rgba(2,107,51,0.14)]'
                        : 'translate-x-0 bg-gradient-to-br from-[#173323] to-[#0c1b13] shadow-[0_5px_14px_rgba(0,0,0,0.28)]'
                      }`}
                    aria-hidden="true"
                  />
                  <span className="relative z-10 flex w-full items-center justify-between px-1">
                    <span className={`transition-colors duration-300 ${isLightTheme ? 'text-[#7c9686]' : 'text-[#ffd86c]'}`}>
                      <Sun size={12} strokeWidth={2.2} />
                    </span>
                    <span className={`transition-colors duration-300 ${isLightTheme ? 'text-[#11251a]' : 'text-[#a3f0c0]'}`}>
                      <Moon size={12} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              )}

              <div onClick={handleLoginClick} ref={profileRef} style={{ zIndex: 1000 }} className="relative">
                <LightTooltip title={(() => { try { const isTrialExplore = sessionStorage.getItem('PG_TRIAL_MODE') === '1'; if (!jwtToken && isPlaygroundRoute && isTrialExplore) return 'Sign Up'; } catch { } return ((jwtToken && firstName) ? firstName : 'Login'); })()} placement="bottom-end">
                  <div className="flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg bg-[#339733] cursor-pointer transition-colors hover:bg-emerald-600 backdrop-blur-sm transition-all duration-300 hover:border-emerald hover:shadow-glow-green">
                    <span className="text-white font-semibold text-[10px] sm:text-xs truncate max-w-[80px] sm:max-w-[120px] block">{(() => { try { const isTrialExplore = sessionStorage.getItem('PG_TRIAL_MODE') === '1'; if (!jwtToken && isPlaygroundRoute && isTrialExplore) return 'Sign Up'; } catch { } return (jwtToken ? (firstName || 'Account') : 'Sign In'); })()}</span>
                    {jwtToken && (
                      <span className="ml-1 inline-block">
                        <svg className={`w-2 h-2 inline transition-transform duration-300 ${dropdownVisible ? 'rotate-180' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 4L6 8L10 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                  </div>
                </LightTooltip>

                {/* Account Dropdown */}
                {jwtToken && dropdownVisible && (
                  <div
                    className="fixed w-48 bg-[#0a0a0a]/95 backdrop-blur-xl rounded-lg shadow-glass border border-white/10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-[9999]"
                    style={{
                      top: profileRef.current ? `${profileRef.current.getBoundingClientRect().bottom + 8}px` : 'auto',
                      right: profileRef.current ? `${window.innerWidth - profileRef.current.getBoundingClientRect().right}px` : 'auto'
                    }}
                  >
                    <div className="py-1">
                      {userRole === 'player' && (
                        <div className="px-4 py-3 text-sm text-white font-semibold hover:bg-emerald/20 cursor-pointer transition-colors" onClick={() => { navigate("/profile/personal_details"); setDropdownVisible(false); }}>
                          Edit Profile
                        </div>
                      )}
                      <div className="px-4 py-3 text-sm text-red-400 font-semibold hover:bg-red-500/10 cursor-pointer transition-colors" onClick={() => { handleLogout(); }}>
                        Logout
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Hamburger Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-0.5 sm:p-1 hover:bg-white/10 rounded-md transition-colors flex-shrink-0"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Dropdown */}
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/10 shadow-2xl animate-in slide-in-from-top-2 duration-200">
              <div className="flex flex-col p-6 gap-4">
                {shouldShowDashboardLink && (
                  <div className="pb-4 border-b border-white/10">
                    <div
                      onClick={() => { navigate(userRole === "player" ? "/game-posts" : "/coach_flow/reviewd-video"); setMobileMenuOpen(false); }}
                      className="text-base font-bold tracking-wide transition-colors text-white/80 hover:text-electric-lime cursor-pointer"
                    >
                      {userRole === "player" ? "My SmartJournal" : "My Reviews"}
                    </div>
                  </div>
                )}
                <NavLink
                  to="/products"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  Products
                </NavLink>
                <NavLink
                  to="/solutions"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  Solutions
                </NavLink>

                <NavLink
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  About Us
                </NavLink>
                <NavLink
                  to="/news"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  News
                </NavLink>
                <NavLink
                  to="/careers"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  Join Our Team
                </NavLink>
                <NavLink
                  to="/support"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `text-base font-bold tracking-wide transition-colors ${isActive ? 'text-electric-lime' : 'text-white/80 hover:text-white'}`}
                >
                  Support
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
